/**
 * The tax invoice's arithmetic — the till's copy of the API's rule (#269,
 * blossom-rest-api#524).
 *
 * **The till cannot ask.** It takes cash with the line down, so it computes
 * every figure itself; the server redoes each step when the sale syncs and
 * refuses a disagreement — after the money is in the drawer. So this is not a
 * rule of the till's own. It is `src/common/money/tax-invoice.ts` in
 * blossom-rest-api, step for step, and `tax-invoice.test.ts` holds it to that
 * file's own table.
 *
 * **Here, in the bridge, because both sides of it need the same answer.** The
 * shop screen shows what will be charged before the cashier takes the money,
 * and the main process records it. Two copies would be two figures that ought
 * to agree; one function is one figure.
 *
 * Integer paise throughout, and the rate in basis points — 18% is 1800. The
 * work is done in `bigint`, as the API does it, so no product here is ever a
 * rounded double; a figure past `Number.MAX_SAFE_INTEGER` is refused rather
 * than carried.
 */

const WHOLE = 10_000n;

/** `numerator / denominator` rounded half up, for non-negative operands. */
const halfUp = (numerator: bigint, denominator: bigint): bigint =>
  (numerator + denominator / 2n) / denominator;

/** Back to a number, or a `RangeError` where a number would not be this figure. */
function paise(value: bigint): number {
  if (value > BigInt(Number.MAX_SAFE_INTEGER) || value < BigInt(Number.MIN_SAFE_INTEGER)) {
    throw new RangeError(`Not representable in paise: ${value.toString()}`);
  }

  return Number(value);
}

function whole(value: number, what: string, least: number): bigint {
  if (!Number.isSafeInteger(value) || value < least) {
    throw new RangeError(`Not a ${what}: ${String(value)}`);
  }

  return BigInt(value);
}

/** One line of a tax invoice, in paise. `amountPaise` is what the line charges. */
export interface TaxedLine {
  readonly taxablePaise: number;
  readonly taxPaise: number;
  readonly amountPaise: number;
}

/**
 * A line sold at a **tax-inclusive** unit price:
 *
 *   1. unit taxable = unit price × 10 000 / (10 000 + rate), rounded half up to
 *      the paisa, **per unit** — 250 / 1.18 = 211.8644 → 211.86
 *   2. line taxable = quantity × the *rounded* unit taxable — × 175 = 37,075.50
 *   3. line tax     = line taxable × rate / 10 000, rounded half up — 6,673.59
 *   4. line amount  = line taxable + line tax — 43,749.09
 *
 * So a taxed line does not charge `quantity × price`, and that is the owner's
 * decision of 27 Sep: the printed amount is the amount charged.
 */
export function taxInclusiveLine(
  unitPricePaise: number,
  quantity: number,
  rateBasisPoints: number,
): TaxedLine {
  const rate = whole(rateBasisPoints, 'tax rate in basis points', 0);
  const count = whole(quantity, 'line quantity', 1);
  const unit = whole(unitPricePaise, 'unit price', 0);

  const taxable = count * halfUp(unit * WHOLE, WHOLE + rate);
  const tax = halfUp(taxable * rate, WHOLE);

  return { taxablePaise: paise(taxable), taxPaise: paise(tax), amountPaise: paise(taxable + tax) };
}

/**
 * What a tax invoice adds to or takes from the sum of its lines to reach a
 * whole rupee: nearest, **half up**, so it lies in `-49 ..= 50`. 43,749.09 →
 * −0.09.
 */
export function invoiceRoundOffPaise(sumPaise: number): number {
  const sum = whole(sumPaise, 'invoice sum', 0);

  return paise(halfUp(sum, 100n) * 100n - sum);
}

/**
 * CGST, and SGST beside it, as the invoice prints them: half the tax, rounded
 * half up — so the two printed halves can sum to a paisa **more** than the tax.
 * The park's own invoice does exactly that (6,673.60 against 6,673.59).
 */
export function gstHalfPaise(taxPaise: number): number {
  if (taxPaise < 0) return -gstHalfPaise(-taxPaise);

  return paise(halfUp(whole(taxPaise, 'tax', 0), 2n));
}

/** What a line is priced from: the held price, and the GST pair on that price row. */
export interface TaxablePrice {
  readonly sellingPaise: number;
  /** Null with the rate when the price is not taxed. */
  readonly hsnCode: string | null;
  /** Hundredths of a percent — 18% is 1800. Null is not taxed, which is not 0%. */
  readonly taxRateBasisPoints: number | null;
}

/**
 * Whether a price row is taxed: both halves of the GST pair. A half pair is
 * untaxed, because the API reads it that way (`lineAmountFor`).
 *
 * Exported because the gate routes on it (#274): admission on a taxed row is a
 * line of a tax invoice, and on an untaxed one is a ticket — so the screen that
 * splits a party and the main process that records it ask the same question.
 */
export const isTaxed = (price: TaxablePrice): boolean =>
  price.hsnCode !== null && price.taxRateBasisPoints !== null;

/** The GST on one line, as `SaleLineResource` records it. */
export interface LineTax {
  readonly hsnCode: string;
  readonly taxRateBasisPoints: number;
  readonly taxablePaise: number;
  readonly taxPaise: number;
}

export interface PricedLine {
  /**
   * What the line lists at, before any bill discount: `taxable + tax` of
   * `taxInclusiveLine` when taxed, `quantity × price` when not. The server's
   * `amount_paise`, which stays the gross.
   */
  readonly amountPaise: number;
  /**
   * This line's share of the bill discount (#352), or 0. When there is one, the
   * tax figures below are on `amountPaise − discountPaise`, not on the gross.
   */
  readonly discountPaise: number;
  readonly tax: LineTax | null;
}

/** The invoice's own figures, as `SaleTaxResource` names them. */
export interface InvoiceTotals {
  /** Σ taxable value of the taxed lines. */
  readonly taxablePaise: number;
  /** Σ tax of the taxed lines. */
  readonly taxPaise: number;
  readonly cgstPaise: number;
  readonly sgstPaise: number;
  /** `totalPaise − Σ lines`, in `-49 ..= 50`. */
  readonly roundOffPaise: number;
}

export interface PricedBasket {
  readonly lines: readonly PricedLine[];
  /** The bill discount the lines share, or 0. */
  readonly discountPaise: number;
  /** What the customer is charged: Σ lines − discount, plus the round off on a tax invoice. */
  readonly totalPaise: number;
  /** `null` when no line is taxed: not a tax invoice, and no tax section to print. */
  readonly invoice: InvoiceTotals | null;
}

/**
 * A bill discount spread over the lines (#352, blossom-rest-api#553): **in
 * proportion to each line's gross amount, to the exact paisa, by largest
 * remainder** — each line takes the floor of its exact share, and the paise
 * left over go one each to the lines with the largest remainders, the earlier
 * line first on a tie. So the parts sum to the discount exactly, and no line
 * takes more than its own amount.
 *
 * A `RangeError` for a discount that is negative, fractional or past the sum.
 */
export function spreadDiscount(amounts: readonly number[], discountPaise: number): number[] {
  const gross = amounts.map((amount) => whole(amount, 'line amount', 0));
  const sum = gross.reduce((total, amount) => total + amount, 0n);
  const discount = whole(discountPaise, 'discount', 0);

  if (discount > sum) {
    throw new RangeError(
      `A discount of ${discount.toString()} is past the bill's ${sum.toString()}`,
    );
  }

  if (discount === 0n) return amounts.map(() => 0);

  const shares = gross.map((amount) => (discount * amount) / sum);
  const remainders = gross.map((amount) => (discount * amount) % sum);
  let left = discount - shares.reduce((total, share) => total + share, 0n);

  // Ties to the earlier line, as `spreadDiscountPaise` breaks them on the server.
  const order = gross
    .map((_, index) => index)
    .sort((a, b) =>
      remainders[b]! > remainders[a]! ? 1 : remainders[b]! < remainders[a]! ? -1 : a - b,
    );

  for (const index of order) {
    if (left === 0n) break;
    shares[index]! += 1n;
    left -= 1n;
  }

  return shares.map(paise);
}

/**
 * A whole basket, priced the way the server will check it.
 *
 * **Any taxed line makes the basket a tax invoice**: its taxable value and tax
 * are summed over the taxed lines, an untaxed line beside them is charged at its
 * sticker price and counts in neither, and the round off takes the whole basket
 * to the rupee. **No taxed line leaves the basket exactly as it always was** —
 * `quantity × price`, summed, never rounded.
 *
 * Untaxed is a line whose price row has no GST pair; a half pair is untaxed too,
 * because the API reads it that way (`lineAmountFor`) — and cannot store one.
 *
 * **A bill discount** (#352) is spread over the lines by {@link spreadDiscount},
 * and each taxed line is then taxed on its net, the tax taken as the remainder
 * — `taxable = halfUp(net × 10000, 10000 + rate)`, `tax = net − taxable` — which
 * is what holds the server's `amount − discount = taxable + tax`. Computing the
 * tax on its own can miss by a paisa, and the sale is refused. The round off is
 * taken from the discounted sum. No discount prices exactly as before.
 *
 * `null` when a figure would not fit in a number, a quantity is not one, or the
 * discount is not a whole number of paise up to the lines' sum: sending such a
 * figure would be sending an amount nobody chose.
 */
export function priceBasket(
  lines: readonly { readonly quantity: number; readonly price: TaxablePrice }[],
  discountPaise = 0,
): PricedBasket | null {
  try {
    const gross = lines.map(({ quantity, price }): PricedLine => {
      const { hsnCode, taxRateBasisPoints: rate, sellingPaise } = price;

      if (hsnCode === null || rate === null) {
        return {
          amountPaise: paise(whole(quantity, 'line quantity', 1) * whole(sellingPaise, 'price', 0)),
          discountPaise: 0,
          tax: null,
        };
      }

      const line = taxInclusiveLine(sellingPaise, quantity, rate);

      return {
        amountPaise: line.amountPaise,
        discountPaise: 0,
        tax: {
          hsnCode,
          taxRateBasisPoints: rate,
          taxablePaise: line.taxablePaise,
          taxPaise: line.taxPaise,
        },
      };
    });

    const shares = spreadDiscount(
      gross.map((line) => line.amountPaise),
      discountPaise,
    );

    // A line that took no share keeps `taxInclusiveLine`'s figures, untouched —
    // the server's `withDiscount` path. Re-taxing it would give the same figures
    // (blossom-rest-api's `bill-discount.spec.ts` asserts so), but this is the
    // path the server takes, step for step.
    const priced =
      discountPaise === 0
        ? gross
        : gross.map((line, index): PricedLine => {
            const share = shares[index]!;

            if (share === 0 || line.tax === null) return { ...line, discountPaise: share };

            const net = BigInt(line.amountPaise - share);
            const taxable = halfUp(net * WHOLE, WHOLE + BigInt(line.tax.taxRateBasisPoints));

            return {
              ...line,
              discountPaise: share,
              tax: { ...line.tax, taxablePaise: paise(taxable), taxPaise: paise(net - taxable) },
            };
          });

    const sum = paise(
      priced.reduce((total, line) => total + BigInt(line.amountPaise), 0n) - BigInt(discountPaise),
    );
    const taxed = priced.flatMap((line) => (line.tax === null ? [] : [line.tax]));

    if (taxed.length === 0) {
      return { lines: priced, discountPaise, totalPaise: sum, invoice: null };
    }

    const taxPaise = paise(taxed.reduce((total, line) => total + BigInt(line.taxPaise), 0n));
    const roundOffPaise = invoiceRoundOffPaise(sum);
    const half = gstHalfPaise(taxPaise);

    return {
      lines: priced,
      discountPaise,
      totalPaise: paise(BigInt(sum) + BigInt(roundOffPaise)),
      invoice: {
        taxablePaise: paise(taxed.reduce((total, line) => total + BigInt(line.taxablePaise), 0n)),
        taxPaise,
        cgstPaise: half,
        sgstPaise: half,
        roundOffPaise,
      },
    };
  } catch (error) {
    if (error instanceof RangeError) return null;

    throw error;
  }
}
